import React, { useContext, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../../App';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { RefreshCw, Loader2, Wand2, ChevronDown, ChevronUp } from 'lucide-react';
import { stringifyError } from '../../../utils/errors';

/**
 * ImageRegenerateButton — inline prompt editor + re-run for an image
 * content section. Renders a compact "Regenerate" pill that expands
 * into a textarea pre-loaded with the section's stored prompt. Admins
 * can tweak the prompt and hit Regenerate to re-run just THAT section
 * through Nano Banana. `onReplace(updatedSection)` swaps the section
 * in place without touching ordering.
 */
const ImageRegenerateButton = ({ lessonId, section, onReplace }) => {
  const { API } = useContext(AuthContext);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [prompt, setPrompt] = useState(
    section?.image_prompt || section?.text_content || section?.image_alt || '',
  );

  const regen = async (opts = {}) => {
    if (!lessonId) {
      toast.error('Save the lesson first so it has an ID.');
      return;
    }
    const body = { section_id: section.id };
    // Only send `prompt` when the admin actually edited it — otherwise
    // let the backend fall back to the stored prompt.
    if (opts.usePrompt && prompt.trim()) body.prompt = prompt.trim();

    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/lms/lessons/${lessonId}/regenerate-image`,
        body,
      );
      if (data?.section) {
        onReplace?.(data.section);
        setPrompt(data.section.image_prompt || prompt);
        toast.success('Image regenerated.');
      }
    } catch (e) {
      toast.error(stringifyError(e, 'Image regeneration failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1">
        <Button
          size="sm"
          variant="outline"
          onClick={() => regen({ usePrompt: false })}
          disabled={busy}
          title="Re-run this image with the stored prompt"
          data-testid={`regen-image-${section.id}`}
        >
          {busy && !expanded
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <RefreshCw className="w-4 h-4" />}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setExpanded((v) => !v)}
          disabled={busy}
          title="Edit prompt before regenerating"
          data-testid={`regen-image-toggle-${section.id}`}
          className="px-2"
        >
          {expanded
            ? <ChevronUp className="w-4 h-4" />
            : <ChevronDown className="w-4 h-4" />}
        </Button>
      </div>
      {expanded && (
        <div
          className="mt-1 w-72 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 p-2 shadow-sm"
          data-testid={`regen-image-editor-${section.id}`}
        >
          <label className="text-[11px] font-semibold text-slate-600 dark:text-neutral-300 uppercase tracking-wide">
            Diagram prompt
          </label>
          <Textarea
            rows={4}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Describe the diagram — cutaway, cross-section, labelled schematic, etc."
            className="text-xs font-mono mt-1"
            data-testid={`regen-image-prompt-${section.id}`}
          />
          <div className="flex items-center justify-end gap-1 mt-1.5">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setPrompt(section?.image_prompt || section?.text_content || '')}
              disabled={busy}
              className="text-xs"
            >
              Reset
            </Button>
            <Button
              size="sm"
              onClick={() => regen({ usePrompt: true })}
              disabled={busy || !prompt.trim()}
              className="bg-amber-500 hover:bg-amber-600 text-white text-xs"
              data-testid={`regen-image-submit-${section.id}`}
            >
              {busy
                ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Regenerating…</>
                : <><Wand2 className="w-3 h-3 mr-1" /> Regenerate with prompt</>}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageRegenerateButton;
